import { MapContainer, Pane, TileLayer, useMap } from 'react-leaflet';
import seg from "../../utils/seg.json";
import L from "leaflet";
import ResponsiveContainer from '../../layouts/ResponsiveContainer';
import icon from '../../assets/icons/openmoji_location-indicator.png';
import { BaseMap } from '../../interfaces/Indicator';
import { useEffect, useRef, useState, useMemo } from 'react';
import NotDataMap from '../Cards/NotDataDisplay';
import useIsMobile from '../../hooks/useIsMobile';
import { messages } from '../../utils/const';
import { IMaps } from '../../interfaces/Maps';
import MapLayers from './MapLayers';
import LayersActive from './LayersActive';

interface geoJSON {
    type: string;
    features: any;
}

const COLOR = '#0E6681';

const convertToGeoJSON = (data: BaseMap[]) => {
    return {
        "type": "FeatureCollection",
        "features": data.map((item) => {
            const { coordenadas, ...restProperties } = item;
            return {
                "type": "Feature",
                "properties": restProperties,
                "geometry": {
                    "type": "Point",
                    "coordinates": coordenadas
                }
            };
        })
    };
};

const setColor = () => ({
    color: COLOR,
    weight: 3,
    opacity: 0.90
});

const MarkersOnMap = () => {
    const map = useMap();
    const markersRef = useRef<{ marker: L.Marker; center: L.LatLng }[]>([]);
    const geoJsonRef = useRef<L.GeoJSON | null>(null);

    const updateMarkers = () => {
        const zoom = map.getZoom();
        const screenWidth = window.innerWidth;

        let baseSize = 20;
        if (screenWidth < 640) baseSize = 16;
        else if (screenWidth < 1024) baseSize = 20;

        const minSize = 4;
        const maxSize = 20;
        const newFontSize = Math.min(Math.max(baseSize * (zoom / 13), minSize), maxSize);

        markersRef.current.forEach(({ marker }) => {
            const el = marker.getElement();
            const div = el?.querySelector('.comuna-text') as HTMLElement;
            if (div) {
                div.style.fontSize = `${newFontSize}px`;
            }
        });
    };

    useEffect(() => {
        if (!map) return;

        const geoJsonLayer = L.geoJSON(seg as any, {
            style: setColor,
            interactive: false
        }) as L.GeoJSON;
        geoJsonRef.current = geoJsonLayer;
        geoJsonLayer.addTo(map);

        const markers: { marker: L.Marker; center: L.LatLng }[] = [];

        geoJsonLayer.eachLayer((layer: any) => {
            if (layer.feature?.properties?.COMUNAS) {
                const comunaNumber = layer.feature.properties.COMUNAS;
                const center = layer.getBounds().getCenter();

                const marker = L.marker(center, {
                    icon: L.divIcon({
                        className: 'comuna-label',
                        html: `<div class="comuna-text" style="font-size: 20px; font-weight: 600; color: ${COLOR};">${comunaNumber}</div>`,
                    }),
                    interactive: false,
                    pane: 'comunaLabels'
                });

                marker.addTo(map);
                markers.push({ marker, center });
            }
        });

        markersRef.current = markers;

        map.on('zoomend', updateMarkers);
        window.addEventListener('resize', updateMarkers);

        return () => {
            map.off('zoomend', updateMarkers);
            window.removeEventListener('resize', updateMarkers);

            markersRef.current.forEach(({ marker }) => {
                marker.remove();
            });
            markersRef.current = [];

            if (geoJsonRef.current) {
                geoJsonRef.current.remove();
                geoJsonRef.current = null;
            }
        };
    }, [map]);

    return null;
};

const GeoJSONLayer = ({ data }: { data: geoJSON }) => {
    const map = useMap();
    const markersRef = useRef<L.Marker[]>([]);
    const geoJsonRef = useRef<L.GeoJSON | null>(null);

    const defaultIcon = useMemo(() => L.icon({
        iconUrl: icon,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
        shadowSize: [41, 41]
    }), []);

    useEffect(() => {
        if (!data || !data.features) return;

        const markers: L.Marker[] = [];

        const pointToLayer = (feature: any, latlng: L.LatLng) => {
            const marker = L.marker(latlng, { icon: defaultIcon });

            if (feature.properties) {
                const { Coordenadas, anio, ...restProperties } = feature.properties;
                let popupContent = "<p>";
                if (anio) {
                    popupContent += `<b>Año</b>:${anio}<br />`;
                }
                for (const [key, value] of Object.entries(restProperties)) {
                    popupContent += `<b>${key}</b>:${value}<br />`;
                }
                popupContent += "</p>";
                marker.bindPopup(popupContent);
            }

            marker.addTo(map);
            markers.push(marker);
            return marker;
        };

        const geoJsonLayer = L.geoJSON(data as any, { pointToLayer });
        geoJsonRef.current = geoJsonLayer;
        markersRef.current = markers;

        return () => {
            markersRef.current.forEach(marker => {
                marker.remove();
            });
            markersRef.current = [];

            if (geoJsonRef.current) {
                geoJsonRef.current.remove();
                geoJsonRef.current.clearLayers();
                geoJsonRef.current = null;
            }
        };
    }, [data, defaultIcon, map]);

    return null;
};

const ZoomHandler = ({ isMobile }: { isMobile: boolean }) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;
        const center = map.getCenter();
        const zoom = isMobile ? 11 : 11.5;
        map.setView(center, zoom);
        map.invalidateSize();
    }, [isMobile, map]);

    return null;
};

const CustomMap = ({ data, mapRef, errorsNoData, chartId, mapLayer, activeCapaIds }: IMaps) => {
    const isMobile = useIsMobile();
    const [dataFormated, setDataFormated] = useState<geoJSON>();
    const [draggingEnabled, setDraggingEnabled] = useState(false);

    useEffect(() => {
        if (data?.length > 0) {
            const geoJSON = convertToGeoJSON(data);
            setDataFormated(geoJSON);
        }
    }, [data]);

    const handleTouchStart = () => setDraggingEnabled(true);
    const handleTouchEnd = () => setDraggingEnabled(false);

    return (
        <ResponsiveContainer className='h-[469px] md:h-[600px]'>
            <div className='h-[469px] md:h-[600px]' style={{ position: "relative" }}>
                <div
                    ref={mapRef}
                    id={`my-node-${chartId ?? 'default'}`}
                    style={{ width: '100%', height: '100%', position: 'relative' }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                >
                    <MapContainer
                        style={{ width: "100%", height: "100%" }}
                        center={[-34.619948411233096, -58.44110846308608]}
                        zoom={isMobile ? 11 : 11.5}
                        scrollWheelZoom={false}
                        preferCanvas={true}
                        dragging={!isMobile || draggingEnabled}
                    >
                        <ZoomHandler isMobile={isMobile} />
                        <Pane name="comunaLabels" style={{ zIndex: 550 }} />
                        <TileLayer
                            attribution='&copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
                            url='https://tile.openstreetmap.org/{z}/{x}/{y}.png'
                        />
                        <MarkersOnMap />
                        {dataFormated && <GeoJSONLayer data={dataFormated} />}
                        {mapLayer && mapLayer.length > 0 && (
                            <MapLayers capasMapa={mapLayer} activeCapaIds={activeCapaIds} />
                        )}
                    </MapContainer>
                    {activeCapaIds && activeCapaIds.length > 0 &&
                        <LayersActive activeCapaIds={activeCapaIds} capasMapa={mapLayer ?? []} />
                    }
                    {errorsNoData ? <NotDataMap message={messages.map} /> : null}
                </div>
            </div>
        </ResponsiveContainer>
    );
};

export default CustomMap;